import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TenantList from './TenantList';
import TenantDetail from './TenantDetail';

/** Default operator surface at /tenants (and /tenants/:id/*).
 *
 * The tenant list is the canonical canvas — operators always see all
 * their tenants here. Clicking one opens the per-tenant detail
 * (Overview / Mailboxes / Domain swap) as a modal layered on top, with
 * a backdrop, X button, and Esc-to-close. The URL still encodes the
 * tenant id (`/tenants/:id`) so links stay shareable and the back
 * button works.
 */
export default function TenantsView() {
  const { id } = useParams();
  const nav = useNavigate();
  const close = () => nav('/tenants');

  // Esc closes the modal — paired with the X button so keyboard-first
  // users aren't stuck on a modal route.
  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Lock background scroll while the modal is open so the long tenant
  // list doesn't move when the modal scrolls.
  useEffect(() => {
    if (!id) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [id]);

  return (
    <>
      <TenantList />
      {id && (
        <div
          className="fixed inset-0 z-40 flex items-stretch justify-center"
          role="dialog" aria-modal="true"
        >
          {/* Backdrop. Click closes. */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
            onClick={close}
          />
          {/* Panel — full-bleed on small screens, capped + centered on wide. */}
          <div
            className="relative z-10 m-auto w-full max-w-5xl h-[min(92vh,920px)] mx-4
                       bg-bg border border-line rounded-xl shadow-modal overflow-hidden
                       animate-slide-up flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <TenantDetail onClose={close} />
          </div>
        </div>
      )}
    </>
  );
}
